import { useRef, useState } from "react";
import { motion, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";

export function MagneticButton({ href, external, children, variant = "primary", size = "md", className, onClick, icon }) {
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const x = useSpring(0, { stiffness: 150, damping: 15, mass: 0.1 });
  const y = useSpring(0, { stiffness: 150, damping: 15, mass: 0.1 });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * 0.25);
    y.set((e.clientY - cy) * 0.25);
  };

  const handleMouseLeave = () => { x.set(0); y.set(0); setIsHovered(false); };

  const base = "group relative inline-flex items-center justify-center gap-2.5 overflow-hidden rounded-full font-medium tracking-tight transition-colors will-change-transform";
  const sizes = { md: "px-6 py-3 text-sm", lg: "px-8 py-4 text-base" };
  const variants = {
    primary: "bg-navy-800 text-white hover:bg-navy-900 shadow-luxury",
    secondary: "bg-white text-navy-800 border border-navy-200 hover:border-navy-300",
    ghost: "bg-transparent text-navy-800 border border-navy-800/20 hover:border-navy-800/50",
  };

  const content = (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{ x, y }}
      className={cn(base, sizes[size], variants[variant], className)}
    >
      {variant === "primary" && (
        <span className={cn("absolute inset-0 -z-10 bg-gradient-to-r from-emerald-400 to-emerald-500 transition-opacity duration-500", isHovered ? "opacity-100" : "opacity-0")} aria-hidden />
      )}
      <span className="relative z-10 flex items-center gap-2">
        {children}
        {icon && <span className="transition-transform duration-500 group-hover:translate-x-1">{icon}</span>}
      </span>
    </motion.div>
  );

  if (!href) return <button onClick={onClick} className="inline-block" type="button">{content}</button>;
  if (external) return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block">{content}</a>;
  return <Link to={href} className="inline-block">{content}</Link>;
}
